import React, { ReactNode, useEffect, useState } from 'react';
import { utils } from 'ethers';
import { tokenDecimals } from '../../../helpers/config/contracts.config';
import { formatCurrencyAmount, getFullDate } from '../../../helpers/formatters';
import { IPositionResponse } from '../../../services/api/api.interfaces';
import LabelValue from '../../LabelValue/LabelValue';
import { TpSlPositionParams } from '../../modals/EditPosition/EditPosition';
import PositionDetailsModal from '../../modals/PositionDetailsModal/PositionDetailsModal';
import {observer} from 'mobx-react';
import './PositionSmallTableRow.scss';

interface IPositionSmallTableRowProps {
    position: IPositionResponse;
    openModal: (modalContent: ReactNode) => void;
    closePosition?: (positionId: number) => any;
    editPosition?: (position: TpSlPositionParams & IPositionResponse) => any;
}

const PositionSmallTableRow: React.FC<IPositionSmallTableRowProps> = observer(({ position, editPosition, closePosition, openModal }) => {

    const openPositionModal = (position: IPositionResponse) => {
        openModal(<PositionDetailsModal closePosition={closePosition} editPosition={editPosition} position={position} />)
    };

    const [tpSlLabel, setTpSlLabel] = useState('');
    const [tpSlValue, setTpSlValue] = useState('');
    const [pnlAmount, setPnlAmount] = useState<number | undefined>();

    useEffect(() => {
        const tp = Number(position.tpPrice);
        const sl = Number(position.slPrice);

        let tpSlLabel = '';
        let tpSlValue = '';

        if (tp && sl) {
            tpSlLabel = 'TP/SL Prices';
            tpSlValue = `${formatCurrencyAmount(tp, '$')}/${formatCurrencyAmount(sl, '$')}`;
        } else if (tp) {
            tpSlLabel = 'TP Price';
            tpSlValue = formatCurrencyAmount(tp, '$');
        } else if (sl) {
            tpSlLabel = 'SL Price';
            tpSlValue = formatCurrencyAmount(sl, '$')
        }

        setTpSlLabel(tpSlLabel);
        setTpSlValue(tpSlValue);
    }, [position.tpPrice, position.slPrice]);

    useEffect(() => {
        const pnlAmount = position.realizedPnL && Number(position.realizedPnL) ? Number(utils.formatUnits(position.realizedPnL, tokenDecimals.usdc)) : position.netPnlAmount;
        setPnlAmount(pnlAmount);
    }, [position.realizedPnL, position.netPnlAmount]);


    return (
        <div onClick={() => openPositionModal(position)} className="PositionSmallTableRow p-2.5 card-container mb-1 cursor-pointer">
            {position.status === 'CLOSED' ? <span className="nereus-caption nereus-color-neutral-grey">{getFullDate(position.finishedAt)}</span> : null}
            <div className="flex mb-1 justify-between nereus-color-white nereus-body">
                <span className="mt-1">{`${position.baseAsset}/${position.quoteAsset}`}</span>
                {position.isShort ? <span className="nereus-color-status-warning">Short {position.leverage}x</span> : <span className="nereus-color-status-up">Long {position.leverage}x</span>}
            </div>
            <hr className="nereus-bg-neutral-dark-blue mt-1.5 mb-1.5"/>
            <LabelValue label="Net PnL" value={pnlAmount ? <span className={pnlAmount < 0 ? 'nereus-color-status-down' : 'nereus-color-status-up'}>{formatCurrencyAmount(pnlAmount || '', 'USDC', 2)}</span> : null} />
            {position.status !== 'CLOSED' && Number(position.lastPrice) ? <LabelValue label="Current Price" value={formatCurrencyAmount(position.lastPrice, '$')} /> : null}
            {position.status !== 'CLOSED' && tpSlLabel ? <LabelValue label={tpSlLabel} value={tpSlValue} /> : null}
            {position.status === 'CLOSED' && Number(position.initialPrice) ? <LabelValue label="Open Price" value={formatCurrencyAmount(position.initialPrice, '$')} /> : null}
            {position.status === 'CLOSED' && Number(position.closedPrice) ? <LabelValue label="Close Price" value={formatCurrencyAmount(position.closedPrice, '$')} /> : null}
        </div>
    );
});

export default PositionSmallTableRow;
